import {
    Modal,
    Grid,
    GridColumn,
    Checkbox,
    InputField,
    View,
    Button,
} from '@fairygodboss/ui';
import { useState, useEffect } from 'react';

import { EmitMetric } from '../../utils/Analytics/VisibilityPixel/VisibilityPixel';
import { submitQuickProfile } from '../../utils/Api/QuickProfile';

import CompanySelectorInput from './components/CompanySelectorInput/CompanySelectorInput';
import { LocationInput } from './components/LocationInput/LocationInput';
import SchoolDegreeSelector from './components/SchoolDegreeSelector/SchoolDegreeSelector';
import SchoolsSelector from './components/SchoolSelector/SchoolSelector';
import { inputStyle, styles } from './QuickProfileStyles';

const SHOW_QUICK_REGISTRATION_KEY = 'quickRegistrationModal';
interface Errors {
    jobTitle: string | undefined;
    jobLocation: string | undefined;
    company: string | undefined;
    school: string | undefined;
    schoolLocation: string | undefined;
    degree: string | undefined;
}

const DefautErrors: Errors = {
    jobTitle: undefined,
    jobLocation: undefined,
    company: undefined,
    school: undefined,
    schoolLocation: undefined,
    degree: undefined,
};

export const QuickProfile = ({ userName }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isStudent, setIsStudent] = useState(false);
    const [jobTitle, setJobTitle] = useState('');
    const [company, setCompany] = useState('');
    const [companyInput, setCompanyInput] = useState('');
    const [jobLocation, setJobLocation] = useState({ value: '' });
    const [school, setSchool] = useState('');
    const [schoolInput, setSchoolInput] = useState('');
    const [schoolLocation, setSchoolLocation] = useState({ value: '' });
    const [degree, setDegree] = useState('');
    const [errorsFound, setErrorsFound] = useState(false);
    const [isRemote, setIsRemote] = useState(false);
    const [formErrors, setFormErrors] = useState(DefautErrors);

    useEffect(() => {
        (async () => {
            const Cookies = (await import('js-cookie')).default;
            const hasQuickRegistrationOnboardingCookie = Cookies.get(
                SHOW_QUICK_REGISTRATION_KEY
            );
            if (hasQuickRegistrationOnboardingCookie) {
                Cookies.remove(SHOW_QUICK_REGISTRATION_KEY);
                setIsModalOpen(true);
            }
        })();
    }, []);
    useEffect(() => {
        if (isModalOpen) {
            EmitMetric({
                misc_event_count: 1,
                misc_event_type: 'quick-profile-modal-impression',
            });
        }
    }, [isModalOpen]);
    const handleSave = async () => {
        EmitMetric({
            misc_event_count: 1,
            misc_event_type: 'quick-profile-modal-submit-click',
        });
        const apiRequestPayload = {
            name: userName,
            experiences: [],
            educations: [],
        };

        let formValid = true;
        setFormErrors({
            jobTitle: undefined,
            jobLocation: undefined,
            company: undefined,
            school: undefined,
            schoolLocation: undefined,
            degree: undefined,
        });
        setErrorsFound(false);
        const errors: Errors = {
            jobTitle: undefined,
            jobLocation: undefined,
            company: undefined,
            school: undefined,
            schoolLocation: undefined,
            degree: undefined,
        };

        if (!isStudent) {
            apiRequestPayload.experiences = [
                {
                    title: jobTitle,
                    company,
                    location: {
                        name: jobLocation.value,
                        ...jobLocation,
                    },
                    isRemote,
                },
            ];
            if (jobTitle === '') {
                errors.jobTitle = 'Job title is a required field.';
                formValid = false;
            }
            if (company === '' || companyInput === '') {
                errors.company = 'Please add or select a company from the dropdown.';
                formValid = false;
            }
            if (!isRemote && jobLocation?.value === '') {
                errors.jobLocation = 'Please enter a valid location.';
                formValid = false;
            }

            setFormErrors(errors);
        } else if (isStudent) {
            apiRequestPayload.educations = [
                {
                    school,
                    location: {
                        name: schoolLocation.value,
                        ...schoolLocation,
                    },
                    degree,
                },
            ];
            if (school === '' || schoolInput === '') {
                errors.school = 'School is a required field.';
                formValid = false;
            }
            if (schoolLocation?.value === '') {
                errors.schoolLocation = 'Please enter a valid location.';
                formValid = false;
            }
            if (degree === '' || degree === 'Select...') {
                errors.degree = 'Degree is a required field.';
                formValid = false;
            }
            setFormErrors(errors);
        }
        if (!formValid) {
            setErrorsFound(!errorsFound);
            return;
        }
        // save the data
        const response = await submitQuickProfile(apiRequestPayload);

        if (response.ok) {
            setIsModalOpen(false);
            return;
        }
        setFormErrors(response.errors);
    };

    const handleStudentToggle = () => {
        setIsStudent(!isStudent);
        setFormErrors(DefautErrors);
        setErrorsFound(!errorsFound);
    };

    const handleJobChange = (e) => {
        setJobTitle(e.target.value);
        setFormErrors({ ...formErrors, jobTitle: undefined });
        setErrorsFound(!errorsFound);
    };

    const handleCompanyChange = (e) => {
        setCompany(e);
        setFormErrors({ ...formErrors, company: undefined });
        setErrorsFound(!errorsFound);
    };

    const handleJobLocationChange = (location) => {
        setJobLocation(location);
        setFormErrors({ ...formErrors, jobLocation: undefined });
        setErrorsFound(!errorsFound);
    };

    const handleSchoolDegreeChange = (value) => {
        setDegree(value);
        setFormErrors({ ...formErrors, degree: undefined });
        setErrorsFound(!errorsFound);
    };

    const handleSchoolChange = (value) => {
        setSchool(value);
        setFormErrors({ ...formErrors, school: undefined });
        setErrorsFound(!errorsFound);
    };

    const handleSchoolInputChange = (e) => {
        setSchoolInput(e);
    };

    const handleCompanyInputChange = (e) => {
        setCompanyInput(e);
    };

    const handleSchoolLocationChange = (value) => {
        setSchoolLocation(value);
        setFormErrors({ ...formErrors, schoolLocation: undefined });
        setErrorsFound(!errorsFound);
    };

    const renderStudentLayout = () => (
        <View className="student-layout">
            <GridColumn
                className={`field-wrapper ${formErrors.school ? 'error' : ''}`}
                pl={0}
                pr={0}
            >
                <div className="form-label">
                    <span>School*</span>
                </div>
                <SchoolsSelector
                    setTypeaheadInput={handleSchoolInputChange}
                    handleSelect={handleSchoolChange}
                    items={[]}
                    handleError={() => null}
                    inputMaxLength={200}
                    allowAddNewItem={true}
                />
                <div className="form-error">
                    <span>{formErrors.school}</span>
                </div>
            </GridColumn>
            <GridColumn
                className={`field-wrapper ${formErrors.degree ? 'error' : ''}`}
                pl={0}
                pr={0}
            >
                <div className="form-label">
                    <span>Degree*</span>
                </div>
                <SchoolDegreeSelector
                    value={degree}
                    onChange={handleSchoolDegreeChange}
                />
                <div className="form-error">
                    <span>{formErrors.degree}</span>
                </div>
            </GridColumn>
            <GridColumn
                className={`field-wrapper ${
                    formErrors.schoolLocation ? 'error' : ''
                }`}
                pl={0}
                pr={0}
            >
                <div className="form-label">
                    <span>School location*</span>
                </div>
                <LocationInput
                    changeHandler={handleSchoolLocationChange}
                    placeholder="City, State"
                    googlePlaceAPITypes={null}
                    googlePlaceAPIComponentRestrictions={null}
                    coordinates={null}
                    value={schoolLocation.value || ''}
                    css={inputStyle}
                />
                <div className="form-error">
                    <span>{formErrors.schoolLocation}</span>
                </div>
            </GridColumn>
            <GridColumn height={85} />
        </View>
    );

    const renderNonStudentLayout = () => (
        <View
            className="non-student-layout"
            css={{
                'div label': {
                    fontWeight: `${isRemote ? 'bold' : 'normal'}`,
                },
            }}
        >
            <GridColumn
                className={`field-wrapper ${
                    formErrors.jobTitle ? 'error' : ''
                }`}
                pl={0}
                pr={0}
            >
                <div className="form-label">
                    <span>
                        {' '}
                        Most recent job title*
                        <span className="job-title-count">
                            {' '}
                            {jobTitle.length}
                            /200
                            {' '}
                        </span>
                    </span>
                </div>
                <InputField
                    value={jobTitle}
                    changeHandler={handleJobChange}
                    css={inputStyle}
                    maxLength={200}
                    placeholder=""
                />
                <div className="form-error">
                    <span>{formErrors.jobTitle}</span>
                </div>
            </GridColumn>
            <GridColumn
                className={`field-wrapper ${formErrors.company ? 'error' : ''}`}
                pl={0}
                pr={0}
            >
                <div className="form-label">
                    <span>Company*</span>
                </div>
                <CompanySelectorInput
                    items={[]}
                    placeholder="Start typing to select a company or add a new one"
                    isMulti={false}
                    inputId="company"
                    allowAPICreation={true}
                    handleError={() => null}
                    withTags={false}
                    className="company-selector-input-form"
                    setTypeaheadInput={handleCompanyInputChange}
                    handleSelect={handleCompanyChange}
                />
                <div className="form-error">
                    <span>{formErrors.company}</span>
                </div>
            </GridColumn>
            <GridColumn
                className={`field-wrapper ${
                    formErrors.jobLocation ? 'error' : ''
                }`}
                pl={0}
                pr={0}
            >
                <div className="form-label">
                    <span>
                        Most recent job location
                        {isRemote ? '' : '*'}
                    </span>
                </div>
                <LocationInput
                    changeHandler={handleJobLocationChange}
                    placeholder="City, State"
                    googlePlaceAPITypes={null}
                    googlePlaceAPIComponentRestrictions={null}
                    coordinates={null}
                    value={jobLocation.value || ''}
                    css={inputStyle}
                />
                <div className="form-error">
                    <span>{formErrors.jobLocation}</span>
                </div>
            </GridColumn>

            <Checkbox
                checked={isRemote}
                onClick={() => {
                    setIsRemote(!isRemote);
                    formErrors.jobLocation = '';
                    setFormErrors({ ...formErrors });
                    setErrorsFound(false);
                }}
                label="Remote"
                className="remote-checkbox"
                style={{
                    borderRadius: '12px',
                    width: '16px',
                    height: '16px',
                }}
                css={{
                    fontWeight: `${isRemote ? 'bold' : 'normal'}`,
                    '.styled-check': {
                        left: '2px',
                    },
                    svg: {
                        position: 'absolute',
                        top: '2px',
                        left: '2px',
                        zIndex: 2,
                        pointerEvents: 'none',
                    },
                }}
            />
        </View>
    );

    return (
        <>
            {isModalOpen ? (
                <Modal
                    isOpen={isModalOpen}
                    isPayWall={true}
                    allowClosing={false}
                    label=""
                    timer={2}
                    onClose={() => {}}
                    onOpen={() => {}}
                >
                    <View
                        className="quick-registration-layout"
                        css={{
                            backgroundColor: '#fff',
                        }}
                    >
                        <style jsx>
                            {`
                                ${styles}
                            `}
                        </style>
                        <View
                            alignItems="center"
                            justifyContent="center"
                            pl={15}
                            pr={15}
                            pt={20}
                            pb={20}
                        >
                            <Grid className="quick-profile-header">
                                <GridColumn className="step-title">
                                    We need a few details to continue.
                                </GridColumn>
                                <GridColumn className="step-subtitle">
                                    (You&apos;ll only have to do this once.)
                                </GridColumn>
                            </Grid>
                            <Grid
                                className={`quick-profile-body ${
                                    errorsFound ? 'errors found' : ''
                                }`}
                            >
                                {!isStudent
                                    ? renderNonStudentLayout()
                                    : renderStudentLayout()}
                            </Grid>
                            <Grid className="quick-profile-footer">
                                <View
                                    className="quick-profile-is-student-switch"
                                    onClick={handleStudentToggle}
                                    css={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    {!isStudent
                                        ? 'I\'m a student'
                                        : 'I\'m not a  student'}
                                </View>
                                <div>
                                    <Button width={175} onPress={handleSave}>
                                        Finish
                                    </Button>
                                </div>
                            </Grid>
                        </View>
                    </View>
                </Modal>
            ) : (
                <></>
            )}
        </>
    );
};
